import { createContext, useContext } from "react";
import type {
  AdditionalService,
  FindFlightOffersFlightSegmentV2,
  FindFlightOffersFlightV2,
  FindFlightOffersOfferFlightsOverviewV2,
  FindFlightOffersOfferV2,
  GetFlightAvailabilityTokenBodyInputDto,
} from "../../types/codegen";
import type { FlightOffer } from "./FlightSelectionPayloadContextProvider";
import type { FlightSearchType } from "../../types/types";

export type AdditionalServiceData = {
  service: AdditionalService;
  mandatory: boolean;
};

export type ServicesSelection = Pick<
  GetFlightAvailabilityTokenBodyInputDto,
  "mandatoryServices" | "optionalServices"
>;

export type FlightType = FindFlightOffersFlightV2 & {
  segments: FindFlightOffersFlightSegmentV2[];
};

export type OfferType = FindFlightOffersOfferV2 & {
  flight: FlightType;
};

export type FullOfferType = Omit<FindFlightOffersOfferV2, "flightsOverview"> & {
  flightsOverview: (FindFlightOffersOfferFlightsOverviewV2 & {
    departureFlight: FlightType;
    returnFlight?: FlightType;
  })[];
} & {
  searchResultToken: string;
};

export type FlightPayload = (FlightOffer & ServicesSelection) | null;

export interface FlightSelectionPayloadContextValue {
  selectedOfferIndex: string | null;
  selectedDepartureFlightId: string | null;
  selectedReturnFlightId: string | null;
  selectedFlightPayload: FlightPayload;
  selectedFlightSearchSource: FlightSearchType | null;
  handleDepartureFlightSelection: (
    offer: FullOfferType,
    flightId: string,
    queryType: FlightSearchType | null,
  ) => void;
  handleReturnFlightSelection: (offer: FullOfferType, flightId: string) => void;
  clearFlightSelection: () => void;
  getTravellerServices: (travellerId: string) => AdditionalServiceData[];
  setTravellerServices: (
    travellerId: string,
    service: AdditionalServiceData[],
  ) => void;
  removeTravellerServices: (travellerId: string) => void;
}

export const FlightSelectionPayloadContext =
  createContext<FlightSelectionPayloadContextValue | null>(null);

export const useFlightSelectionPayloadContext = () => {
  const context = useContext(FlightSelectionPayloadContext);
  if (!context) {
    throw new Error(
      "useFlightSelectionPayloadContext must be used within a FlightSelectionPayloadContextProvider",
    );
  }
  return context;
};
